import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Users,
  Building2,
  Target,
  Megaphone,
  TrendingUp,
  TrendingDown,
  Activity,
} from 'lucide-react';
import {
  AreaChart,
  Area,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  Cell,
} from 'recharts';
import { apiClient } from '@/lib/api';

interface KpiMetric {
  total: number;
  trend: number;
}

interface PipelineStage {
  id: string;
  name: string;
  color: string;
  position: number;
  count: number;
  totalValue: number;
  weightedValue: number;
}

interface ConversionRateData {
  total: number;
  won: number;
  lost: number;
  rate: number;
}

interface DailyCount {
  day: string;
  count: number;
}

interface RecentActivity {
  id: string;
  type: string;
  description: string;
  created_at: string;
  lead_title: string;
  lead_id: string;
  user_name: string;
}

interface ActiveCampaignDashboard {
  id: string;
  name: string;
  type: string;
  status: string;
  stats: {
    total: number;
    contacted: number;
    responded: number;
    converted: number;
  };
  startDate: string;
  endDate: string;
}

export interface MobileDashboardData {
  kpi: {
    contacts: KpiMetric;
    companies: KpiMetric;
    leads: KpiMetric;
    campaigns: KpiMetric;
    sparklines: {
      contacts7d: number[];
      leads7d: number[];
      companies7d: number[];
      campaigns7d: number[];
    };
  };
  pipeline: PipelineStage[];
  conversionRate: ConversionRateData;
  leadsDaily30d: DailyCount[];
  recentActivities: RecentActivity[];
  activeCampaigns: ActiveCampaignDashboard[];
  weightedPipelineValue: number;
}

interface MobileCRMDashboardProps {
  onActivityTap?: (leadId: string) => void;
  /** Bumping `refreshKey` triggers a re-fetch (used by pull-to-refresh). */
  refreshKey?: number;
  /** Notifies the parent when a new dashboard payload is loaded. Used so the
   * Campagne tab can enrich its list with activity stats. */
  onData?: (data: MobileDashboardData) => void;
}

function formatCurrency(value: number) {
  if (!Number.isFinite(value)) return '€ 0';
  if (Math.abs(value) >= 1000) {
    return `€ ${(value / 1000).toFixed(1)}k`;
  }
  return `€ ${Math.round(value)}`;
}

function relativeTime(iso: string): string {
  try {
    const d = new Date(iso).getTime();
    const diff = Date.now() - d;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'ora';
    if (m < 60) return `${m} min fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h fa`;
    const days = Math.floor(h / 24);
    if (days < 7) return `${days}g fa`;
    return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

export default function MobileCRMDashboard({
  onActivityTap,
  refreshKey = 0,
  onData,
}: MobileCRMDashboardProps) {
  const [data, setData] = useState<MobileDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setError(null);
      const response = await apiClient.get<MobileDashboardData>('/crm/dashboard-analytics');
      // The api client signature claims `ApiResponse<T>` but the request method
      // returns the parsed JSON body verbatim. The dashboard endpoint replies
      // with the data shape directly (no `success/data` wrapper).
      const payload =
        response && typeof response === 'object' && 'kpi' in (response as object)
          ? (response as unknown as MobileDashboardData)
          : ((response as unknown as { data?: MobileDashboardData })?.data ?? null);
      if (!payload) {
        setError('Risposta non valida');
        return;
      }
      setData(payload);
      onData?.(payload);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, [onData]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (error || !data) {
    return (
      <div className="p-6 text-center" data-testid="mobile-dashboard-error">
        <Activity className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
        <p className="text-sm text-muted-foreground mb-4">
          {error || 'Nessun dato disponibile'}
        </p>
        <button
          type="button"
          onClick={() => {
            setLoading(true);
            void load();
          }}
          className="min-h-[44px] px-4 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
        >
          Riprova
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-6" data-testid="mobile-dashboard">
      {/* KPI 2x2 grid */}
      <section className="grid grid-cols-2 gap-3">
        <KpiCard
          label="Contatti"
          value={data.kpi.contacts.total}
          trend={data.kpi.contacts.trend}
          icon={Users}
          spark={data.kpi.sparklines.contacts7d}
          accent="primary"
        />
        <KpiCard
          label="Aziende"
          value={data.kpi.companies.total}
          trend={data.kpi.companies.trend}
          icon={Building2}
          spark={data.kpi.sparklines.companies7d}
          accent="emerald"
        />
        <KpiCard
          label="Lead"
          value={data.kpi.leads.total}
          trend={data.kpi.leads.trend}
          icon={Target}
          spark={data.kpi.sparklines.leads7d}
          accent="amber"
        />
        <KpiCard
          label="Campagne"
          value={data.kpi.campaigns.total}
          trend={data.kpi.campaigns.trend}
          icon={Megaphone}
          spark={data.kpi.sparklines.campaigns7d}
          accent="violet"
        />
      </section>

      {/* Pipeline */}
      <section className="rounded-xl border border-border bg-card p-4" data-testid="mobile-dashboard-pipeline">
        <header className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-foreground">Pipeline</h3>
          <span className="text-xs text-muted-foreground">
            {formatCurrency(data.weightedPipelineValue || 0)} ponderato
          </span>
        </header>
        {data.pipeline.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">Nessuno stage configurato</p>
        ) : (
          <PipelineFunnel stages={data.pipeline} />
        )}
      </section>

      {/* Conversion + 30g trend */}
      <section className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-card p-4" data-testid="mobile-dashboard-conversion">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Conversione
          </h3>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-foreground tabular-nums">
              {data.conversionRate.rate}
            </span>
            <span className="text-sm text-muted-foreground">%</span>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-1 text-[11px]">
            <div className="text-emerald-600 dark:text-emerald-400">
              {data.conversionRate.won} vinti
            </div>
            <div className="text-destructive">{data.conversionRate.lost} persi</div>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4" data-testid="mobile-dashboard-trend">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
            Lead 30g
          </h3>
          <Trend30dChart data={data.leadsDaily30d} />
        </div>
      </section>

      {/* Recent activity */}
      <section className="rounded-xl border border-border bg-card" data-testid="mobile-dashboard-activity">
        <header className="px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold text-foreground">Attività recente</h3>
        </header>
        <div className="divide-y divide-border">
          {data.recentActivities.length === 0 ? (
            <p className="text-xs text-muted-foreground p-4 text-center">Nessuna attività</p>
          ) : (
            data.recentActivities.slice(0, 6).map((act) => (
              <button
                key={act.id}
                type="button"
                onClick={() => onActivityTap?.(act.lead_id)}
                className="w-full min-h-[44px] text-left px-4 py-3 flex items-start gap-3 active:bg-accent transition-colors"
                data-testid={`mobile-dashboard-activity-${act.id}`}
              >
                <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                  <Activity className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground line-clamp-2">{act.description}</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    {act.lead_title || 'Lead'} · {act.user_name || 'Sistema'} · {relativeTime(act.created_at)}
                  </p>
                </div>
              </button>
            ))
          )}
        </div>
      </section>
    </div>
  );
}

const ACCENT_MAP = {
  primary: { ring: 'text-primary', bg: 'bg-primary/10', stroke: 'hsl(var(--primary))' },
  emerald: { ring: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-500/10', stroke: '#10b981' },
  amber: { ring: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-500/10', stroke: '#f59e0b' },
  violet: { ring: 'text-violet-600 dark:text-violet-400', bg: 'bg-violet-500/10', stroke: '#7c3aed' },
} as const;

function KpiCard({
  label,
  value,
  trend,
  icon: Icon,
  spark,
  accent,
}: {
  label: string;
  value: number;
  trend: number;
  icon: typeof Users;
  spark: number[];
  accent: keyof typeof ACCENT_MAP;
}) {
  const colors = ACCENT_MAP[accent];
  const sparkData = useMemo(
    () => (spark || []).map((v, i) => ({ i, v })),
    [spark],
  );
  const trendUp = trend >= 0;
  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid={`mobile-dashboard-kpi-${label.toLowerCase()}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-8 h-8 rounded-lg ${colors.bg} ${colors.ring} flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <div
          className={`flex items-center gap-0.5 text-[11px] font-medium ${
            trendUp
              ? 'text-emerald-600 dark:text-emerald-400'
              : 'text-destructive'
          }`}
        >
          {trendUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
          <span>{Math.abs(trend)}%</span>
        </div>
      </div>
      <div className="text-xl font-bold text-foreground tabular-nums">
        {value.toLocaleString('it-IT')}
      </div>
      <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{label}</div>
      {sparkData.length > 1 && (
        <div className="h-7 -mx-1 mt-1.5">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={sparkData}>
              <Area
                type="monotone"
                dataKey="v"
                stroke={colors.stroke}
                fill={colors.stroke}
                fillOpacity={0.18}
                strokeWidth={1.5}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function PipelineFunnel({ stages }: { stages: PipelineStage[] }) {
  const max = Math.max(1, ...stages.map((s) => s.count));
  return (
    <div className="space-y-2">
      {stages
        .slice()
        .sort((a, b) => a.position - b.position)
        .map((s) => {
          const pct = Math.round((s.count / max) * 100);
          return (
            <div key={s.id} className="flex items-center gap-2">
              <span className="text-xs text-foreground truncate flex-1 max-w-[40%]">{s.name}</span>
              <div className="flex-1 h-6 rounded-md bg-muted overflow-hidden relative">
                <div
                  className="h-full transition-all"
                  style={{
                    width: `${Math.max(pct, 4)}%`,
                    backgroundColor: s.color || 'hsl(var(--primary))',
                    opacity: 0.9,
                  }}
                />
                <span className="absolute inset-0 flex items-center px-2 text-[11px] font-semibold text-foreground">
                  {s.count}
                </span>
              </div>
            </div>
          );
        })}
    </div>
  );
}

function Trend30dChart({ data }: { data: DailyCount[] }) {
  const chart = useMemo(
    () => data.slice(-14).map((d) => ({ day: d.day, v: d.count })),
    [data],
  );
  if (chart.every((d) => d.v === 0)) {
    return <p className="text-[11px] text-muted-foreground py-4 text-center">Nessun lead</p>;
  }
  return (
    <div className="h-20">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chart}>
          <XAxis dataKey="day" hide />
          <Tooltip
            cursor={{ fill: 'hsl(var(--accent))' }}
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: 6,
              fontSize: 11,
            }}
            labelFormatter={(l) => `${l}`}
            formatter={(v) => [`${Number(v ?? 0)} lead`, '']}
          />
          <Bar dataKey="v" radius={[2, 2, 0, 0]}>
            {chart.map((_, i) => (
              <Cell key={i} fill="hsl(var(--primary))" />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function DashboardSkeleton() {
  return (
    <div className="space-y-4 pb-6" data-testid="mobile-dashboard-skeleton">
      <div className="grid grid-cols-2 gap-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="rounded-xl border border-border bg-card p-3 h-[110px] animate-pulse">
            <div className="w-8 h-8 rounded-lg bg-muted mb-3" />
            <div className="h-5 bg-muted rounded w-2/3 mb-1" />
            <div className="h-3 bg-muted rounded w-1/2" />
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-border bg-card p-4 h-40 animate-pulse" />
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-xl border border-border bg-card p-4 h-28 animate-pulse" />
        <div className="rounded-xl border border-border bg-card p-4 h-28 animate-pulse" />
      </div>
      <div className="rounded-xl border border-border bg-card p-4 h-48 animate-pulse" />
    </div>
  );
}
